import { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { createApiClient } from '../services/apiClient';

const NotificationContext = createContext();

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const getApiClient = useCallback(() => {
    const token = sessionStorage.getItem('token');
    return token ? createApiClient(token) : null;
  }, []);

  const fetchNotifications = useCallback(async () => {
    const client = getApiClient();
    if (!client) return;
    setLoading(true);
    try {
      const { data } = await client.get('/notifications?limit=50');
      setNotifications(data.notifications || []);
    } catch (err) {
      console.error('[Notifications] fetch failed:', err.message);
    } finally {
      setLoading(false);
    }
  }, [getApiClient]);

  // Poll every 30 seconds for new notifications
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllRead = useCallback(async () => {
    const client = getApiClient();
    if (!client) return;
    try {
      await client.put('/notifications/read-all');
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error('[Notifications] markAllRead failed:', err.message);
    }
  }, [getApiClient]);

  const markOneRead = useCallback(async (id) => {
    const client = getApiClient();
    if (!client) return;
    try {
      await client.put(`/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
    } catch (err) {
      console.error('[Notifications] markOneRead failed:', err.message);
    }
  }, [getApiClient]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read).length,
    [notifications]
  );

  const value = useMemo(
    () => ({ notifications, unreadCount, loading, fetchNotifications, markAllRead, markOneRead }),
    [notifications, unreadCount, loading, fetchNotifications, markAllRead, markOneRead]
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
